import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import { DEFAULT_FONT_FAMILY, FONT_REGISTRY, findFontEntry } from './font-registry';

/** Пункт выбора шрифта. `style.fontFamily` рисует пункт его же шрифтом — список служит превью. */
export interface FontOption {
  value: string;
  label: string;
  style: { fontFamily: string };
}

/**
 * Что пишется в объект при выборе: медиа-менеджер хранит голое имя (`Oswald`), трансляции —
 * CSS-стек (`"Oswald", sans-serif`). Сохранённые значения не мигрируются, поэтому оба формата
 * читаются одинаково — по первому семейству стека (`primaryFontFamily`).
 */
export type FontValueFormat = 'family' | 'stack';

/** Первое семейство CSS-стека без кавычек: `"Open Sans", sans-serif` → `Open Sans`. */
export function primaryFontFamily(stack: string | undefined | null): string {
  if (!stack) return '';
  const first = stack.split(',')[0] ?? '';
  return first
    .trim()
    .replace(/^["']|["']$/g, '')
    .trim();
}

/**
 * Стек для `fontFamily` с запасным шрифтом записи. Имя всегда в кавычках: без них `Exo 2` —
 * не валидное имя семейства в CSS, и канва молча рисует запасным.
 */
export function toFontStack(family: string): string {
  const name = primaryFontFamily(family);
  const fallback = findFontEntry(name)?.fallback ?? 'sans-serif';
  return `"${name}", ${fallback}`;
}

/** Стек шрифта новых объектов. */
export const DEFAULT_FONT_STACK = toFontStack(DEFAULT_FONT_FAMILY);

export function fontPickerOptions(format: FontValueFormat = 'family'): FontOption[] {
  return FONT_REGISTRY.filter((entry) => entry.inPicker).map((entry) => ({
    value: format === 'stack' ? toFontStack(entry.family) : entry.family,
    label: entry.family,
    style: { fontFamily: toFontStack(entry.family) },
  }));
}

/** Пункт списка, который соответствует значению объекта, — по первому семейству, без учёта регистра. */
export function findFontOption<T extends { value: string }>(options: readonly T[], value: string | undefined | null): T | undefined {
  const family = primaryFontFamily(value).toLowerCase();
  if (!family) return undefined;
  return options.find((option) => primaryFontFamily(option.value).toLowerCase() === family);
}

/** Подпись шрифта старой схемы, которого нет в наборе: пустой селект при живом значении путает. */
export function missingFontLabel(value: string): MessageDescriptor {
  const family = primaryFontFamily(value);
  return msg`${family} — нет в наборе`;
}

/**
 * Список выбора с текущим значением: если значения нет в наборе, оно добавляется отдельным пунктом
 * с подписью `label` (обычно `t(missingFontLabel(value))`). Значение не подменяется — шрифт
 * сохранённого табло остаётся как был.
 */
export function withCurrentFont<T extends { value: string }>(
  options: T[],
  value: string | undefined | null,
  label: string,
): (T | FontOption)[] {
  if (!value || findFontOption(options, value)) return options;
  return [...options, { value, label, style: { fontFamily: value } }];
}
